import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import Editor from '@monaco-editor/react';
import p5 from 'p5';
import { findErrorLine, setupMonacoWithP5Types, createCustomConsole, createErrorHandlingWrapper, setupAssetPathRedirection } from '@/components/EditorUtils';
import { Save, Image } from 'lucide-react';
import { useTheme } from '@/lib/theme-provider';

/**
 * Composant principal d'édition de code avec p5.js
 * @param {Object} props - Propriétés du composant
 * @param {string} props.initialCode - Code initial à afficher
 * @param {string} props.mobileMode - Mode d'affichage mobile ("editor", "canvas" ou null pour desktop)
 * @param {Function} props.onPlayClick - Fonction appelée quand le bouton Play est cliqué en mode mobile
 * @param {Function} props.onCodeChange - Fonction appelée quand le code est modifié
 * @param {Function} props.onError - Fonction appelée quand une erreur est détectée
 * @param {Function} props.onSave - Fonction appelée quand le bouton Save est cliqué
 * @param {Function} props.onAssetsClick - Fonction appelée quand le bouton Assets est cliqué
 * @param {string} props.workdir - repertoire pour rediriger les chemins d'assets
 * @param {Object} ref - Référence exposant des méthodes contrôlées par useImperativeHandle
 */
const EditorFull = forwardRef(({ initialCode, mobileMode, onPlayClick, onCodeChange, onError, onSave, onAssetsClick, workdir }, ref) => {
  const [code, setCode] = useState(initialCode || '');
  const [logs, setLogs] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const canvasRef = useRef(null);
  const sketchInstance = useRef(null);
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const errorDecorations = useRef([]);
  const errorRef = useRef(false); // Référence pour suivre si une erreur a été détectée
  const preventDefaultSaveRef = useRef(null);
  const { theme } = useTheme();

  // Exposer les méthodes à travers la ref
  useImperativeHandle(ref, () => ({
    runSketch: () => runSketch(),
    getCode: () => code,
    highlightError: (lineNumber, errorMessage) => {
      highlightErrorLine(lineNumber);
    },
    cleanupSketch: () => {
      cleanupSketch();
      setIsRunning(false);
    }
  }));

  // Mettre à jour le code si initialCode change
  useEffect(() => {
    if (initialCode !== code) {
      setCode(initialCode);
    }
  }, [initialCode]);

  // Effet pour nettoyer l'écouteur Ctrl+S lors du démontage
  useEffect(() => {
    // Fonction pour empêcher le comportement par défaut du navigateur pour Ctrl+S
    const preventDefaultSave = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        // Appeler la fonction de sauvegarde si disponible
        if (onSave) {
          onSave();
        }
        return false;
      }
    };
    
    // Ajouter l'écouteur d'événement au document
    document.addEventListener('keydown', preventDefaultSave);
    preventDefaultSaveRef.current = preventDefaultSave;
    
    return () => {
      // Nettoyer l'écouteur lors du démontage du composant
      document.removeEventListener('keydown', preventDefaultSave);
    };
  }, [onSave]);

  // Fonction pour configurer Monaco avec les typages p5.js
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    
    // Utiliser la fonction partagée pour configurer Monaco
    setupMonacoWithP5Types(editor, monaco);
    
    // Supprimer l'action de blocage du copier-coller si elle existe
    try {
      const action = editor.getAction('block-paste');
      if (action) {
        editor.removeAction('block-paste');
      }
    } catch (e) {
      // Ignorer si l'action n'existe pas
    }
  };


  // Fonction pour ajouter une décoration d'erreur sur la ligne
  const highlightErrorLine = (lineNumber) => {
    if (!editorRef.current || !monacoRef.current || !lineNumber) return;
    
    // Supprimer les décorations existantes
    if (errorDecorations.current.length > 0) {
      errorDecorations.current = editorRef.current.deltaDecorations(errorDecorations.current, []);
    }
    
    // Ajouter la nouvelle décoration
    errorDecorations.current = editorRef.current.deltaDecorations([], [
      {
        range: new monacoRef.current.Range(lineNumber, 1, lineNumber, 1),
        options: {
          isWholeLine: true,
          className: 'myErrorLine'
        }
      }
    ]);
  };

  // Fonction pour effacer les décorations d'erreur
  const clearErrorHighlights = () => {
    if (editorRef.current && errorDecorations.current.length > 0) {
      errorDecorations.current = editorRef.current.deltaDecorations(errorDecorations.current, []);
    }
  };

  const showErrorInConsole = (errorLine, errorMessage, stackTrace, p) => {
    // Ne pas ajouter de message si une erreur est déjà signalée
    if (errorRef.current) {
      return;
    }
    
    // Marquer qu'une erreur a été signalée
    errorRef.current = true;
    // Remettre le bouton play car le jeu ne tourne pas vraiment
    setIsRunning(false);

    let message = errorLine 
      ? `Erreur ligne ${errorLine}: ${errorMessage}`
      : `Erreur: ${errorMessage}`;

    if(stackTrace)console.log(stackTrace);

    // Mettre en surbrillance la ligne d'erreur dans l'éditeur
    highlightErrorLine(errorLine);
    
    // Notifier le parent de l'erreur
    if (onError) {
      onError({
        lineNumber: errorLine,
        message: errorMessage,
        stackTrace: stackTrace
      });
    }

    // Ajouter l'erreur et les noms de fonctions aux logs
    setLogs(prevLogs => {
      const newLogs = [...prevLogs, message];
      return newLogs;
    });
    
    // Nettoyage complet des canvas sans réinitialiser les logs
    cleanupSketch();
    
    // Maintenant créer un nouveau canvas avec l'erreur
    setTimeout(() => {
      // Vérifier une dernière fois qu'il n'y a pas de canvas avant d'en créer un nouveau
      document.querySelectorAll('canvas[id^="defaultCanvas"]').forEach(canvas => {
        if (canvas && canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      });
      
      const errorSketch = (p) => {
        p.setup = function() {
          p.createCanvas(600, 600);
          p.background(50);
          p.fill(255);
          p.textSize(16);
          p.textAlign(p.CENTER, p.CENTER);
          p.text(message, p.width/2, p.height/2);
        }
        p.draw = function() {} // Fonction vide
        p.noLoop(); // Important: arrêter la boucle draw
      };
      
      sketchInstance.current = new p5(errorSketch, canvasRef.current);
    }, 0);
  };
  

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupSketch();
    };
  }, []);

  const cleanupSketch = () => {
    if (sketchInstance.current) {
      try {
        // Nettoyer explicitement tous les event handlers
        const p = sketchInstance.current;
        
        // Supprimer les fonctions d'événements pour éviter qu'elles continuent à s'exécuter
        p.mousePressed = function() {};
        p.mouseReleased = function() {};
        p.mouseMoved = function() {};
        p.keyPressed = function() {};
        p.keyReleased = function() {};
        p.draw = function() {}; // Arrêter la boucle draw
        
        // Arrêter la boucle si elle tourne encore
        if (typeof p.noLoop === 'function') {
          p.noLoop();
        }
        
        // Supprimer complètement l'instance
        sketchInstance.current.remove();
      } catch (e) {
        console.error("Erreur lors du nettoyage du sketch:", e);
      }
      sketchInstance.current = null;

      if (canvasRef.current) {
        while (canvasRef.current.firstChild) {
          canvasRef.current.removeChild(canvasRef.current.firstChild);
        }
      }
    }
  };

  // Une fonction séparée pour réinitialiser les logs
  const resetLogs = () => {
    setLogs([]);
  };

  // Gestionnaire de changement de code pour effacer les erreurs
  const handleCodeChange = (newCode) => {
    setCode(newCode);
    // Informer le parent du changement de code
    if (onCodeChange) {
      onCodeChange(newCode);
    }
    // Effacer les décorations d'erreur quand l'utilisateur modifie le code
    clearErrorHighlights();
  };

  const runSketch = () => {
    // Si en mode mobile "editor" et qu'une fonction onPlayClick est fournie, l'appeler
    if (mobileMode === "editor" && onPlayClick) {
      onPlayClick();
      return;
    }
    
    cleanupSketch();
    resetLogs(); // Réinitialiser les logs seulement au lancement d'un nouveau sketch
    errorRef.current = false; // Réinitialiser l'état d'erreur
    setIsRunning(true);
    
    // Réinitialiser les décorations d'erreur précédentes
    clearErrorHighlights();

    const sketchFn = (p) => {
      try {
        const wrappedCode = `${code}\n//# sourceURL=userCode.js`;

        // Créer un objet console personnalisé pour capturer les logs
        const customConsole = createCustomConsole(log => {
          setLogs(prevLogs => [...prevLogs, log]);
        });

        const userFn = new Function('p', 'console', `
          with(p) {
            // Définir print comme alias de console.log
            const print = (...args) => console.log(...args);
            ${wrappedCode}
            return {
              preload: typeof preload === 'function' ? preload : null,
              setup: typeof setup === 'function' ? setup : null,
              draw: typeof draw === 'function' ? draw : null,
              mousePressed: typeof mousePressed === 'function' ? mousePressed : null,
              mouseReleased: typeof mouseReleased === 'function' ? mouseReleased : null,
              mouseMoved: typeof mouseMoved === 'function' ? mouseMoved : null,
              keyPressed: typeof keyPressed === 'function' ? keyPressed : null,
              keyReleased: typeof keyReleased === 'function' ? keyReleased : null
            };
          }
        `);

        const userFunctions = userFn(p, customConsole);

        // Callback d'erreur pour les wrappers
        const errorCallback = (errorLine, errorMessage, stack) => {
          showErrorInConsole(errorLine, errorMessage, stack, p);
        };

        // Configurer la redirection des chemins d'assets avant de créer les wrappers
        if (workdir) {
          setupAssetPathRedirection(p,workdir, errorCallback);
        }

        // Appliquer les wrappers aux fonctions p5.js
        // Fonction preload - doit être appelée en premier pour charger les assets
        if (userFunctions.preload) {
          p.preload = createErrorHandlingWrapper(
            () => userFunctions.preload.call(p),
            "preload",
            errorCallback,
            false // Ne pas arrêter la loop en cas d'erreur dans preload
          );
        }

        p.setup = createErrorHandlingWrapper(
          function() {
            if (userFunctions.setup) {
              userFunctions.setup.call(p);
            } 
          }, 
          "setup",
          errorCallback,
          false // Ne pas arrêter la loop en cas d'erreur dans setup
        );

        p.draw = createErrorHandlingWrapper(
          function() {
            if (userFunctions.draw) {
              userFunctions.draw.call(p);
            }
          }, 
          "draw", 
          errorCallback,
          true // Arrêter la loop en cas d'erreur
        );

        // Appliquer les wrappers aux autres fonctions d'événements si elles existent
        if (userFunctions.mousePressed) {
          p.mousePressed = createErrorHandlingWrapper(
            () => userFunctions.mousePressed.call(p),
            "mousePressed",
            errorCallback
          );
        }
        
        if (userFunctions.mouseReleased) {
          p.mouseReleased = createErrorHandlingWrapper(
            () => userFunctions.mouseReleased.call(p),
            "mouseReleased",
            errorCallback
          );
        }
        
        if (userFunctions.mouseMoved) {
          p.mouseMoved = createErrorHandlingWrapper(
            () => userFunctions.mouseMoved.call(p),
            "mouseMoved",
            errorCallback
          );
        }
        
        if (userFunctions.keyPressed) {
          p.keyPressed = createErrorHandlingWrapper(
            () => userFunctions.keyPressed.call(p),
            "keyPressed",
            errorCallback
          );
        }
        
        if (userFunctions.keyReleased) {
          p.keyReleased = createErrorHandlingWrapper(
            () => userFunctions.keyReleased.call(p),
            "keyReleased",
            errorCallback
          );
        }

      } catch (error) {
        console.error("Erreur:", error);
        const errorLineNumber = findErrorLine(code); // Utiliser la fonction partagée
        showErrorInConsole(errorLineNumber, error.message, error.stack, p);
        return false; // Ne pas relancer l'erreur
      }
    };

    setTimeout(() => {
      sketchInstance.current = new p5(sketchFn, canvasRef.current);
    }, 0);
  };

  // Système de redimensionnement adaptatif du canvas - simplifié comme EditorHalf
  useEffect(() => {
    const centerAndScaleCanvas = () => {
      if (canvasRef.current) {
        const canvases = canvasRef.current.querySelectorAll('canvas');
        if (canvases.length > 0) {
          const canvas = canvases[0];
          
          // Obtenir les dimensions originales du canvas
          const originalWidth = canvas.width;
          const originalHeight = canvas.height;
          
          if (originalWidth === 0 || originalHeight === 0) return; // Éviter la division par zéro
          
          // Calculer l'aspect ratio original
          const aspectRatio = originalWidth / originalHeight;
          
          // Obtenir les dimensions du conteneur canvas
          const container = canvasRef.current.parentElement;
          if (!container) return;
          
          const containerRect = container.getBoundingClientRect();
          
          // Calculer l'espace disponible avec marges
          let availableWidth = containerRect.width - 32; // 16px de marge de chaque côté
          let availableHeight = containerRect.height - 32;
          
          // Calculer les dimensions en préservant l'aspect ratio
          let newWidth, newHeight;
          
          if (availableWidth / aspectRatio <= availableHeight) {
            // Limité par la largeur
            newWidth = Math.min(availableWidth, originalWidth);
            newHeight = newWidth / aspectRatio;
          } else {
            // Limité par la hauteur
            newHeight = Math.min(availableHeight, originalHeight);
            newWidth = newHeight * aspectRatio;
          }
          
          // S'assurer que les dimensions ne dépassent pas les originales
          if (newWidth > originalWidth || newHeight > originalHeight) {
            newWidth = originalWidth;
            newHeight = originalHeight;
          }
          
          // Arrondir les dimensions
          newWidth = Math.floor(newWidth);
          newHeight = Math.floor(newHeight);
          
          canvas.style.display = 'block';
          canvas.style.margin = 'auto';
          canvas.style.width = `${newWidth}px`;
          canvas.style.height = `${newHeight}px`;
          canvas.style.maxWidth = '100%';
          canvas.style.maxHeight = '100%';
          canvas.style.borderRadius = '8px';
          canvas.style.overflow = 'hidden';
          canvas.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
          
          //console.log(`Canvas redimensionné: ${originalWidth}x${originalHeight} → ${newWidth}x${newHeight} (container: ${containerRect.width}x${containerRect.height})`);
        }
      }
    };

    // Observer le conteneur pour détecter quand un canvas est ajouté
    let observer;
    if (canvasRef.current) {
      observer = new MutationObserver(centerAndScaleCanvas);
      observer.observe(canvasRef.current, { childList: true, subtree: true });
    }

    // Appliquer le centrage et redimensionnement immédiatement si un canvas existe déjà
    centerAndScaleCanvas();
    
    // Écouter les changements de taille de la fenêtre pour redimensionner - délai réduit
    const handleResize = () => {
      setTimeout(centerAndScaleCanvas, 50); // Délai réduit pour plus de réactivité
    };
    
    window.addEventListener('resize', handleResize);

    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [mobileMode]); // Dépendance sur mobileMode pour relancer quand le mode change

  const stopSketch = () => {
    cleanupSketch();
    setIsRunning(false);
  };

  // Mode mobile "editor" pour le bouton en haut à droite
  if (mobileMode === "editor") {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 relative">
          <div className="overflow-hidden h-full rounded-lg">
            <Editor
              height="100%"
              defaultLanguage="javascript"
              value={code}
              onChange={handleCodeChange}
              onMount={handleEditorDidMount}
              theme={theme === 'dark' ? "vs-dark" : "vs"}
              options={{
                minimap: { enabled: false },
                fontSize: 16,
                fontFamily: "monospace",
                wordWrap: 'on',
                tabSize: 2,
                scrollBeyondLastLine: false,
                lineNumbers: 'on',
                folding: false,
                autoIndent: true,
                suggestOnTriggerCharacters: true,
                semanticHighlighting: true,
                overviewRulerLanes: 0,
                hideCursorInOverviewRuler: true,
                overviewRulerBorder: false,
                selectionHighlight: false,
                occurrencesHighlight: false,
                matchBrackets: 'never',
                renderLineHighlight: 'none',
                stickyScroll: {
                  enabled: false
                }
              }}
            />
          </div>

          {/* Boutons Play, Assets et Save - Toujours afficher le bouton play en mode mobile */}
          <div className="absolute top-3 right-6 z-30 flex flex-col gap-2">
            <button
              onClick={onPlayClick}
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md"
              title="Exécuter le code"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="currentColor" 
                className="w-5 h-5 ml-0.5"
              >
                <path d="M8 5.14v14l11-7-11-7z" />
              </svg>
            </button>
            
            {/* Bouton Assets */}
            {onAssetsClick && (
              <button
                onClick={onAssetsClick}
                className="bg-purple-500 hover:bg-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md"
                title="Gérer les assets"
              >
                <Image className="w-5 h-5" />
              </button>
            )}
            
            {/* Bouton Save - caché mais toujours accessible */}
            {onSave && (
              <button
                onClick={onSave}
                className="hidden bg-green-500 hover:bg-green-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md"
                title="Sauvegarder le code"
              >
                <Save className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Si en mode mobile "canvas", n'afficher que le canvas et la console
  if (mobileMode === "canvas") {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex flex-col gap-4" style={{ height: '100%' }}>
          {/* Canvas - prend 2/3 de l'espace */}
          <div 
            className="bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center"
            style={{ flex: '2 1 0', minHeight: 0 }}
          >
            <div
              ref={canvasRef}
              className="canvas-container"
            />
          </div>
          
          {/* Console - prend 1/3 de l'espace (deux fois plus petite que le canvas) - HAUTEUR FIXE */}
          <div 
            className={`${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-800'} font-mono text-sm overflow-y-auto p-3 rounded-lg`}
            style={{ flex: '1 0 0', minHeight: 0, maxHeight: '33.333vh' }}
            ref={(el) => {
              if (el) {
                el.scrollTop = el.scrollHeight;
              }
            }}
          >
            <div className="font-bold mb-2">Console:</div>
            {logs.map((log, index) => (
              <div key={index} className="whitespace-pre-wrap break-words">
                {log}
              </div>
            ))}
          </div>

          {/* Bouton Play/Stop mobile pour l'onglet Game */}
          <button
            onClick={isRunning ? stopSketch : runSketch}
            className={`fixed bottom-6 right-6 z-30 ${isRunning ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg`}
            title={isRunning ? "Arrêter le code" : "Exécuter le code"}
          >
            {isRunning ? (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="currentColor" 
                className="w-6 h-6"
              >
                <rect x="6" y="6" width="12" height="12" />
              </svg>
            ) : (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="currentColor" 
                className="w-6 h-6 ml-0.5"
              >
                <path d="M8 5.14v14l11-7-11-7z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Mode desktop par défaut
  return (
    <div style={{ height: 'calc(100vh - 130px)', maxHeight: 'calc(100vh - 130px)', padding: '0px', display: 'flex', gap: '16px', overflow: 'hidden' }}>
        {/* Éditeur (côté gauche) - ULTRA SIMPLE */}
        <div style={{ width: '50%', height: '100%', position: 'relative' }}>
          <div style={{ height: '100%', borderRadius: '8px', overflow: 'hidden' }}>
            <Editor
              height="100%"
              defaultLanguage="javascript"
              value={code}
              onChange={handleCodeChange}
              onMount={handleEditorDidMount}
              theme={theme === 'dark' ? "vs-dark" : "vs"}
              options={{
                minimap: { enabled: false },
                fontSize: 16,
                fontFamily: "monospace",
                wordWrap: 'on',
                tabSize: 2,
                scrollBeyondLastLine: false,
                lineNumbers: 'on',
                folding: false,
                autoIndent: true,
                suggestOnTriggerCharacters: true,
                semanticHighlighting: true,
                overviewRulerLanes: 0,
                hideCursorInOverviewRuler: true,
                overviewRulerBorder: false,
                selectionHighlight: false,
                occurrencesHighlight: false,
                matchBrackets: 'never',
                renderLineHighlight: 'none',
                stickyScroll: {
                  enabled: false
                }
              }}
            />
          </div>

          {/* Boutons Play/Stop, Assets et Save */}
          <div style={{ position: 'absolute', top: '12px', right: '24px', zIndex: 30, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button
              onClick={isRunning ? stopSketch : runSketch}
              className={`${isRunning ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md`}
              title={isRunning ? "Arrêter le code" : "Exécuter le code"}
            >
              {isRunning ? (
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="currentColor" 
                  className="w-5 h-5"
                >
                  <rect x="6" y="6" width="12" height="12" />
                </svg>
              ) : (
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="currentColor" 
                  className="w-5 h-5 ml-0.5"
                >
                  <path d="M8 5.14v14l11-7-11-7z" />
                </svg>
              )}
            </button>
            
            {/* Bouton Assets */}
            {onAssetsClick && (
              <button
                onClick={onAssetsClick}
                className="bg-purple-500 hover:bg-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md"
                title="Gérer les assets"
              >
                <Image className="w-5 h-5" />
              </button>
            )}
            
            {/* Bouton Save - caché visuellement mais toujours accessible via onSave */}
            {onSave && (
              <button
                onClick={onSave}
                className="hidden bg-green-500 hover:bg-green-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md"
                title="Sauvegarder le code"
              >
                <Save className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Partie droite (canvas et console) - ULTRA SIMPLE */}
        <div style={{ width: '50%', height: '100%', display: 'flex', flexDirection: 'column', gap: '18px' }}>
          {/* Canvas - prend un peu moins d'espace */}
          <div 
            className="bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center"
            style={{ height: '66%' }}
          >
            <div
              ref={canvasRef}
              className="canvas-container"
            />
          </div>
          
          {/* Console - encore plus grosse */}
          <div 
            className={`${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-neutral-100 text-gray-800'} font-mono text-sm overflow-y-auto p-3 rounded-lg`}
            style={{ height: '34%' }}
            ref={(el) => {
              if (el) {
                el.scrollTop = el.scrollHeight;
              }
            }}
          >
            <div className="font-bold mb-2">Console:</div>
            {logs.map((log, index) => (
              <div key={index} className="whitespace-pre-wrap break-words">
                {log}
              </div>
            ))}
          </div>
        </div>
    </div>
  );
});

export default EditorFull; 